import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { 
  Grid, 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  Box, 
  FormControl,
  InputLabel, 
  Select, 
  MenuItem,
  Checkbox,
  ListItemText,
  Button 
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { supabase } from '../supabaseClient';
import { useSecteur } from '../contexts/SecteurContext';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { formatDuration } from '../utils/timeUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function Dashboard() {
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  const [selectedActivities, setSelectedActivities] = useState([]); // Ajout du state pour les activités sélectionnées

  // Générer les options de période (12 derniers mois)
  const periodOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  }, []);

  const [activityData, setActivityData] = useState({
    labels: [],
    datasets: []
  });

  const [dotationData, setDotationData] = useState({
    labels: [],
    datasets: []
  });

  const [consommationData, setConsommationData] = useState({
    labels: [],
    datasets: []
  });

  const [consommationParActiviteData, setConsommationParActiviteData] = useState({
    labels: [],
    datasets: []
  });

  const [nombreActivitesData, setNombreActivitesData] = useState({
    labels: [],
    datasets: []
  });

  const [stockData, setStockData] = useState({});

  const [soldeData, setSoldeData] = useState({});

  const [releveCodes, setReleveCodes] = useState([]);

  const [dataWithUsers, setDataWithUsers] = useState([]);

  const { selectedSecteur } = useSecteur();

  const transformDataForChart = React.useCallback((data, releveCodesData) => {
    // Générer les jours du mois sélectionné
    const [year, month] = selectedPeriod.split('-');
    const daysInMonth = new Date(year, month, 0).getDate();
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const chartData = {};
    const colors = ['rgba(25, 118, 210, 0.5)', 'rgba(255, 99, 132, 0.5)', 'rgba(75, 192, 192, 0.5)'];

    // Initialiser les données par jour
    data.forEach(pointage => {
      const pointageDate = new Date(pointage.date);
      if (pointageDate.getMonth() + 1 === parseInt(month) && 
          pointageDate.getFullYear() === parseInt(year)) {
        const day = pointageDate.getDate();
        const releveCode = pointage.releve_codes.code;
        const role = pointage.releve_codes.role;
        const key = `${releveCode}-${role}`;

        if (!chartData[key]) {
          chartData[key] = new Array(daysInMonth).fill(0);
        }
        chartData[key][day - 1] += pointage.hours;
      }
    });

    return {
      labels: days,
      datasets: Object.keys(chartData).map((key, index) => {
        const [releveCode, role] = key.split('-');
        const releveCodeData = releveCodesData.find(code => 
          code.code === releveCode && code.role === role);
        return {
          label: `${releveCode} (${role})`,
          data: chartData[key],
          backgroundColor: releveCodeData?.color || colors[index % colors.length],
        };
      }),
    };
  }, [selectedPeriod]);

  const calculateConsommationParActiviteChart = React.useCallback((data, dataWithUsers) => {
    const chartData = {};
    const colors = ['rgba(25, 118, 210, 0.5)', 'rgba(255, 99, 132, 0.5)', 'rgba(75, 192, 192, 0.5)', 'rgba(255, 206, 86, 0.5)', 'rgba(153, 102, 255, 0.5)', 'rgba(255, 159, 64, 0.5)'];

    // Extraire le mois et l'année de la période sélectionnée
    const [year, month] = selectedPeriod.split('-').map(Number);
    const selectedDate = new Date(year, month - 1);

    // Filtrer les pointages pour le mois sélectionné
    const filteredData = data.filter(pointage => {
        const pointageDate = new Date(pointage.date);
        return pointageDate.getMonth() === selectedDate.getMonth() &&
               pointageDate.getFullYear() === selectedDate.getFullYear();
    });

    // Générer les jours du mois
    const daysInMonth = new Date(year, month, 0).getDate();
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    // Traiter les données filtrées
    filteredData.forEach(pointage => {
        const pointageDate = new Date(pointage.date);
        const day = pointageDate.getDate();
        const activityName = pointage.activity?.name || 'Inconnue';
        const hours = pointage.hours;

        if (!chartData[activityName]) {
            chartData[activityName] = new Array(daysInMonth).fill(0);
        }

        chartData[activityName][day - 1] += hours;
    });

    // Transformer en format pour le graphique
    return {
        labels: days,
        datasets: Object.keys(chartData).map((key, index) => ({
            label: key,
            data: chartData[key],
            backgroundColor: colors[index % colors.length],
        })),
    };
}, [selectedPeriod]);

  const calculateNombreActivitesChartMemo = React.useCallback((data, dataWithUsers) => {
    const chartData = {};
    const colors = ['rgba(25, 118, 210, 0.5)', 'rgba(255, 99, 132, 0.5)', 'rgba(75, 192, 192, 0.5)', 'rgba(255, 206, 86, 0.5)', 'rgba(153, 102, 255, 0.5)', 'rgba(255, 159, 64, 0.5)'];

    // Extraire le mois et l'année de la période sélectionnée
    const [year, month] = selectedPeriod.split('-').map(Number);
    const selectedDate = new Date(year, month - 1);

    // Modifier le filtrage pour inclure les faits marquants indépendamment du filtre d'activités
    const filteredData = data.filter(pointage => {
        const pointageDate = new Date(pointage.date);
        const isInSelectedPeriod = pointageDate.getMonth() === selectedDate.getMonth() &&
                                 pointageDate.getFullYear() === selectedDate.getFullYear();
        const isSelectedActivity = pointage.on_report || // Toujours inclure les faits marquants
                                 selectedActivities.length === 0 || 
                                 selectedActivities.includes(pointage.activity?.name);
        return isInSelectedPeriod && isSelectedActivity;
    });

    // Générer les jours du mois
    const daysInMonth = new Date(year, month, 0).getDate();
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    // Traiter les données filtrées
    filteredData.forEach(pointage => {
        const pointageDate = new Date(pointage.date);
        const day = pointageDate.getDate();
        const activityName = pointage.activity?.name || 'Inconnue';

        if (!chartData[activityName]) {
            chartData[activityName] = new Array(daysInMonth).fill(0);
        }

        chartData[activityName][day - 1] += 1; // Compter le nombre d'activités
    });

    // Transformer en format pour le graphique
    return {
        labels: days,
        datasets: Object.keys(chartData).map((key, index) => ({
            label: key,
            data: chartData[key],
            backgroundColor: colors[index % colors.length],
        })),
    };
}, [selectedPeriod, selectedActivities]);

  const fetchActivityData = React.useCallback(async () => {
    try {
      
      if (!selectedSecteur) {
        console.warn('Aucun secteur sélectionné');
        return;
      }

      // Calculer la date de début et de fin du mois sélectionné
      const [year, month] = selectedPeriod.split('-');
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);

      // Étape 1: Charger les mandats du secteur sélectionné
      const { data: mandates, error: mandateError } = await supabase
        .from('user_mandates')
        .select('id, releve_code_id, user_id')
        .eq('sector_id', selectedSecteur);

      if (mandateError) {
        console.error('Erreur lors de la récupération des mandats:', mandateError);
        throw mandateError;
      }

      // Étape 2: Charger les codes de relève associés à ces mandats
      const { data: releveCodesData, error: releveError } = await supabase
        .from('releve_codes')
        .select('id, code, hours_per_month, color, particularity, role') // Ajouter 'role' ici
        .in('id', mandates.map(m => m.releve_code_id));

      if (releveError) throw releveError;

      releveCodesData.sort((a, b) => a.code.localeCompare(b.code)); // Trier par code de relève
      setReleveCodes(releveCodesData); // Mettre à jour l'état avec les releveCodes récupérés

      // Étape 3: Charger les pointages associés à ces mandats
      const { data, error } = await supabase
        .from('pointage')
        .select(`
          date, 
          hours, 
          releve_code_id, 
          releve_codes(code, role), 
          user_mandate_id, 
          user_mandates(user_id), 
          activity(name),
          description,
          on_report
        `)
        .in('user_mandate_id', mandates.map(m => m.id))
        .gte('date', startDate.toISOString())
        .lte('date', endDate.toISOString());

      if (error) throw error;

      

      // Récupérer les noms des utilisateurs après avoir récupéré les user_id
      const userIds = data.map(pointage => pointage.user_mandates.user_id);
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, full_name')
        .in('id', userIds);

      if (userError) throw userError;



      // Ajouter les noms des utilisateurs aux données
      const dataWithUsers = data.map(pointage => {
        const user = userData.find(u => u.id === pointage.user_mandates.user_id);
        return { ...pointage, users: { full_name: user ? user.full_name : 'Inconnu' } };
      }).sort((a, b) => a.releve_codes.code.localeCompare(b.releve_codes.code)); // Trier par code de relève

      setDataWithUsers(dataWithUsers);

      // Transformez les données pour l'histogramme empilé
      const formattedData = transformDataForChart(dataWithUsers, releveCodesData);
      setActivityData(formattedData);

      // Nouveau graphique pour les consommations par activité
      const consommationParActiviteChart = calculateConsommationParActiviteChart(data, dataWithUsers);
      setConsommationParActiviteData(consommationParActiviteChart);

      // Nouveau graphique pour le nombre d'activités
      const nombreActivitesChart = calculateNombreActivitesChartMemo(data, dataWithUsers);
      setNombreActivitesData(nombreActivitesChart);

      // Calculer les données pour les graphiques de dotations et consommations
      const consommationChart = calculateConsommationChart(releveCodesData, dataWithUsers);
      setConsommationData(consommationChart);
    } catch (error) {
      console.error('Erreur lors de la récupération des données d activité:', error);
    }
  }, [selectedPeriod, selectedSecteur, calculateConsommationParActiviteChart, transformDataForChart, calculateNombreActivitesChartMemo]); // Ajouter les dépendances

  const calculateDotationChart = (stockData, releveCodesData) => {
    const chartData = {
      labels: Object.keys(stockData),
      datasets: [
        {
          label: 'Dotations',
          data: Object.values(stockData).map(releveCode =>
            Object.values(releveCode.users).reduce((acc, user) => acc + user.dotation, 0) // Somme des dotations par utilisateur
          ),
          backgroundColor: Object.keys(stockData).map(code =>
            releveCodesData.find(rc => rc.code === code.split('-')[0] && rc.role === code.split('-')[1])?.color || 'rgba(0,0,0,0.5)'), // Appliquer la couleur du releve_code ou une couleur par défaut
        },
      ],
    };

    return chartData;
  };

  const calculateConsommationChart = (releveCodesData, pointages) => {
    const chartData = {
      labels: releveCodesData.map(releveCode => `${releveCode.code} (${releveCode.role})`), // Ajouter le rôle au label
      datasets: [
        {
          label: 'Consommations',
          data: releveCodesData.map(releveCode => {
            const userPointages = pointages.filter(p => p.releve_code_id === releveCode.id);
            return userPointages.reduce((acc, pointage) => acc + pointage.hours, 0);
          }),
          backgroundColor: releveCodesData.map(releveCode => releveCode.color), // Utiliser la couleur du releveCode
        },
      ],
    };

    return chartData;
  };

  const calculateStockTableMemo = React.useCallback((releveCodesData, pointages) => {
    const stockTable = {};
  
    // First, initialize the structure
    releveCodesData.forEach(releveCode => {
      const code = releveCode.code;
      const role = releveCode.role;
      const key = `${code}-${role}`;
  
      if (!stockTable[key]) {
        stockTable[key] = {
          dotation: 0,
          consommation: 0,
          stock: 0,
          users: {},
          particularity: releveCode.particularity,
        };
      }
    });
  
    // Process each pointage to build user data and calculate consumptions
    pointages.forEach(pointage => {
      const releveCode = releveCodesData.find(rc => rc.id === pointage.releve_code_id);
      if (!releveCode) return;
  
      const code = releveCode.code;
      const role = releveCode.role;
      const key = `${code}-${role}`;
      const userId = pointage.user_mandate_id;
  
      // Initialize user if not exists
      if (!stockTable[key].users[userId]) {
        let monthlyDotation = releveCode.hours_per_month || 0;
        // Diviser par 12 si la particularité est 'annual'
        if (releveCode.particularity === 'annual') {
          monthlyDotation = monthlyDotation / 12;
        }
  
        stockTable[key].users[userId] = {
          consommation: 0,
          dotation: monthlyDotation,
          full_name: pointage.users.full_name,
          particularity: releveCode.particularity,
        };
        // Add to total dotation when adding a new user
        stockTable[key].dotation += monthlyDotation;
      }
  
      // Add consumption
      const pointageDate = new Date(pointage.date);
      const [year, month] = selectedPeriod.split('-').map(Number);
      const selectedDate = new Date(year, month - 1);
  
      if (pointageDate.getMonth() === selectedDate.getMonth() &&
          pointageDate.getFullYear() === selectedDate.getFullYear()) {
        stockTable[key].users[userId].consommation += pointage.hours;
        stockTable[key].consommation += pointage.hours;
      }
    });
  
    // Calculate final stocks
    Object.keys(stockTable).forEach(key => {
      stockTable[key].stock = stockTable[key].dotation - stockTable[key].consommation;
  
      // Calculate user stocks
      Object.keys(stockTable[key].users).forEach(userId => {
        const user = stockTable[key].users[userId];
        user.stock = user.dotation - user.consommation;
      });
    });
  
    return stockTable;
  }, [selectedPeriod]);

  const calculateSoldeChart = (stockData, releveCodes) => {
    const chartData = {
      labels: Object.keys(stockData),
      datasets: [
        {
          label: 'Soldes',
          data: Object.values(stockData).map(item => item.stock),
          backgroundColor: Object.keys(stockData).map(key =>
            releveCodes.find(rc => rc.code === key.split('-')[0] && rc.role === key.split('-')[1])?.color || 'rgba(0,0,0,0.5)'), // Appliquer la couleur du releve_code ou une couleur par défaut
        },
      ],
    };

    return chartData;
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const faitsMarquantsElement = document.getElementById('faits-marquants');
    const graphiqueElement = document.getElementById('graphique-faits-marquants');

    // Exporter la section des faits marquants
    const faitsMarquantsCanvas = await html2canvas(faitsMarquantsElement);
    const faitsMarquantsImgData = faitsMarquantsCanvas.toDataURL('image/png');
    doc.addImage(faitsMarquantsImgData, 'PNG', 10, 10, 190, 0);

    // Ajouter une nouvelle page pour le graphique des faits marquants
    doc.addPage();
    const graphiqueCanvas = await html2canvas(graphiqueElement);
    const graphiqueImgData = graphiqueCanvas.toDataURL('image/png');
    doc.addImage(graphiqueImgData, 'PNG', 10, 10, 190, 0);

    doc.save('rapport_activite.pdf');
  };

  // Ajouter cette fonction pour gérer le changement de sélection des activités
  const handleActivitySelection = (event) => {
    setSelectedActivities(event.target.value);
  };

  useEffect(() => {
    fetchActivityData();
  }, [fetchActivityData]);

  useEffect(() => {
    const stockTable = calculateStockTableMemo(releveCodes, dataWithUsers); // Calculer les données du tableau des soldes
    setStockData(stockTable);

    const dotationChart = calculateDotationChart(stockTable, releveCodes); // Mettre à jour le graphique des dotations
    setDotationData(dotationChart);

    const soldeChart = calculateSoldeChart(stockTable, releveCodes); // Mettre à jour le graphique des soldes
    setSoldeData(soldeChart);
  }, [releveCodes, dataWithUsers, calculateStockTableMemo]);

  useEffect(() => {
    if (dataWithUsers.length > 0) {
        const nombreActivitesChart = calculateNombreActivitesChartMemo(dataWithUsers, dataWithUsers);
        setNombreActivitesData(nombreActivitesChart);
    }
}, [dataWithUsers, calculateNombreActivitesChartMemo]);

  return (
    <div className="dashboard-container">
      <Grid container spacing={3}>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            Activités mensuelles
          </Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              displayEmpty
              size="small"
            >
              {periodOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            color="primary"
            onClick={exportToPDF}
            startIcon={<PictureAsPdfIcon />}
            sx={{ 
              ml: 2,
              textTransform: 'none',
              boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 0 10px rgba(255,255,255,0.2)' : 2,
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.main',
              color: (theme) => theme.palette.mode === 'dark' ? 'primary.contrastText' : 'white',
              '&:hover': {
                boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 0 15px rgba(255,255,255,0.3)' : 4,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'primary.main' : 'primary.dark'
              }
            }}
          >
            Rapport d'activité
          </Button>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>

            {/* Afficher les soldes par code */}
            <Grid item xs={12} md={12} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <Grid container spacing={2} alignItems="center" sx={{ height: '100%', minWidth: '120px' }}>
                {Object.entries(stockData)
                  .filter(([key, data]) => data.stock > 0)
                  .slice(0, 3)
                  .map(([key, data]) => {
                    const [code, role] = key.split('-');
                    return (
                      <Grid item key={key} xs={4} sx={{ textAlign: 'center', minWidth: '120px' }}>
                        <Paper style={{
                          marginBottom: '8px',
                          padding: '8px',
                          backgroundColor: (releveCodes.find(r => r.code === code && r.role === role) || {}).color || 'transparent'
                        }}>
                          <Typography color={'white'} variant="h4">{`${code}`}</Typography>
                          <Typography color={'white'} variant="h6">{`(${role})`}</Typography>
                          <Typography color={'white'} variant="h8">Solde</Typography>
                          <Typography color={'white'} variant="h4">{formatDuration(data.stock)}</Typography>
                        </Paper>
                      </Grid>
                    );
                  })}
              </Grid>
            </Grid>


            <Grid xs={12} md={12} >



              <Typography variant="h6" gutterBottom>
                Solde du secteur
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Code Relève</TableCell>
                    <TableCell>Dotation en h</TableCell>
                    <TableCell>Conso en h</TableCell>
                    <TableCell>Solde en h</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(stockData).map((key, index) => {
                    const [code, role] = key.split('-'); // Séparer le code et le rôle
                    const totalDotation = parseInt(stockData[key].dotation, 10); // Convertir en nombre
                    const consommation = parseInt(stockData[key].consommation, 10); // Convertir en nombre
                    const solde = totalDotation - consommation; // Calculer le solde

                    return (
                      <TableRow key={index}>
                        <TableCell>{`${code} (${role})`}</TableCell>
                        <TableCell>{formatDuration(totalDotation)}</TableCell>
                        <TableCell>{formatDuration(consommation)}</TableCell>
                        <TableCell>{formatDuration(solde)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>




              <Typography variant="h6" gutterBottom>
                Graphiques de Dotation, Consommation et Solde
              </Typography>

              <Grid xs={12} md={12} sx={{ display: 'flex', flexDirection: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {/* Afficher les Graphiques de Dotation, Consommation et Solde */}

                <Grid md={4} >
                  <Typography variant="subtitle1">Dotations</Typography>

                  <Bar data={dotationData} options={{ responsive: true }} />

                </Grid>

                <Grid md={4}  >
                  <Typography variant="subtitle1">Consommations</Typography>

                  <Bar data={consommationData} options={{ responsive: true }} />

                </Grid>

                <Grid md={4} >
                  <Typography variant="subtitle1">Solde</Typography>
                  {soldeData && soldeData.datasets ?

                    <Bar data={soldeData} options={{ responsive: true }} />

                    :
                    <Typography variant="subtitle1">Chargement des données...</Typography>
                  }
                </Grid>

              </Grid>

            </Grid>




            {/* Afficher le graphique de consommation par code*/}
            <Grid>
              <Typography variant="h6" gutterBottom>
                Consommation du secteur
              </Typography>

              <Bar data={activityData} options={{ responsive: true }} />


            </Grid>


            {/* Afficher le graphique de consommation par activité */}
            <Grid >
              <Typography variant="h6" gutterBottom>
                Consommation par activité
              </Typography>

              <Bar data={consommationParActiviteData} options={{ responsive: true }} />

            </Grid>


          </Paper>

        </Grid>





        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Soldes  par utilisateur
            </Typography>
            {Object.keys(stockData).map((key, index) => {
              const [code, role] = key.split('-'); // Séparer le code et le rôle
              return (
                <div key={index} >
                  <div key={index} style={{ backgroundColor: (releveCodes.find(r => r.code === code && r.role === role) || {}).color || 'transparent' }}>
                    <Typography align="center" variant="h6" color={'white'} sx={{ mt: 5 }} gutterBottom>{`${code} (${role})`}</Typography> {/* Afficher le code et le rôle */}
                  </div>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Membre</TableCell>
                        <TableCell>Dotation en h</TableCell>
                        <TableCell>Conso en h</TableCell>
                        <TableCell>Solde en h</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.keys(stockData[key].users).map((userId, userIndex) => (
                        <TableRow key={userIndex}>
                          <TableCell>{stockData[key].users[userId].full_name}</TableCell>
                          <TableCell>{formatDuration(stockData[key].users[userId].dotation)}</TableCell>
                          <TableCell>{formatDuration(stockData[key].users[userId].consommation)}</TableCell>
                          <TableCell>{formatDuration(stockData[key].users[userId].stock)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              );
            })}
          </Paper>
        </Grid>





        {/* Afficher le graphique des activités journalières */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }} id="graphique-faits-marquants">
            <Typography variant="h6" gutterBottom>
              Activités journalières
            </Typography>
            {nombreActivitesData.datasets && <Bar data={nombreActivitesData} options={{ responsive: true }} />}
          </Paper>
        </Grid>


        {/* Nouvelle section pour le Fait Marquant */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }} id="faits-marquants">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Activités détaillées
              </Typography>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Filtrer par activité</InputLabel>
                <Select
                  multiple
                  value={selectedActivities}
                  onChange={handleActivitySelection}
                  renderValue={(selected) => selected.join(', ')}
                  size="small"
                >
                  {Array.from(new Set(dataWithUsers.map(p => p.activity?.name)))
                    .filter(name => name)
                    .sort()
                    .map((activityName) => (
                      <MenuItem key={activityName} value={activityName}>
                        <Checkbox checked={selectedActivities.indexOf(activityName) > -1} />
                        <ListItemText primary={activityName} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>

            {Object.entries(
              dataWithUsers
                .filter(p => {
                  const pointageDate = new Date(p.date);
                  const [year, month] = selectedPeriod.split('-').map(Number);
                  const isInSelectedPeriod = pointageDate.getMonth() === month - 1 && 
                                          pointageDate.getFullYear() === year;
                  const isSelectedActivity = p.on_report || // Toujours inclure les faits marquants
                                          selectedActivities.length === 0 || 
                                          selectedActivities.includes(p.activity?.name);
                  return isInSelectedPeriod && isSelectedActivity;
                })
                .reduce((groups, pointage) => {
                  const activityName = pointage.activity?.name || 'Sans activité';
                  if (!groups[activityName]) {
                    groups[activityName] = [];
                  }
                  groups[activityName].push(pointage);
                  return groups;
                }, {})
            ).map(([activityName, groupedPointages]) => (
              <Box key={activityName} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {activityName}
                </Typography>
                {[...groupedPointages]
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map((pointage) => (
                    <Box
                      key={pointage.id}
                      sx={{
                        mb: 1,
                        pl: 2,
                        borderLeft: '4px solid',
                        borderColor: (releveCodes.find(r => 
                          r.code === pointage.releve_codes.code && 
                          r.role === pointage.releve_codes.role
                        ) || {}).color || 'transparent',
                        backgroundColor: pointage.on_report ? 'action.hover' : 'background.paper',
                        p: 1,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="body2">
                        {format(new Date(pointage.date), 'dd/MM/yyyy')} : {pointage.description}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            ))}
          </Paper>
        </Grid>


      </Grid >
    </div >
  );
}

export default Dashboard;